import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Eye, EyeOff, Mail, Lock, Key } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from '@/components/SEO';

// Use window.location.origin to ensure we always call the correct backend
const API = `${window.location.origin}/api`;
const LOGO_URL = "/images/dea-wordmark.png";

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [adminSecret, setAdminSecret] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [name, setName] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    try {
      const userData = await login(email, password);
      // Check if user is admin
      const checkRes = await axios.get(`${API}/admin/check`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (checkRes.data.is_admin) {
        toast.success('Welcome to Admin Dashboard!');
        navigate('/admin/dashboard');
      } else {
        toast.error('You do not have admin access');
        localStorage.removeItem('token');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!email || !password || !name || !adminSecret) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/register`, {
        email,
        password,
        name,
        admin_secret: adminSecret
      });
      
      localStorage.setItem('token', response.data.access_token);
      toast.success('Admin account created!');
      navigate('/admin/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO 
        title="Admin Portal"
        description="Dea Admin Portal - Manage users, invitations, and analytics."
        noindex={true}
      />
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="glass-card rounded-2xl p-8 space-y-6">
          <div className="text-center">
            <Link to="/" className="inline-block mb-6">
              <img src={LOGO_URL} alt="Dea" className="h-12 w-auto object-contain mx-auto" />
            </Link>
            <div className="flex items-center justify-center gap-2 mb-2">
              <Shield className="h-6 w-6 text-[#D4AF37]" />
              <h1 className="font-serif text-2xl text-[#1A1A1A]">Admin Portal</h1>
            </div>
            <p className="text-[#4A4A4A] text-sm">
              {isRegistering ? 'Create your admin account' : 'Sign in to access the dashboard'}
            </p>
          </div>

          <form onSubmit={isRegistering ? handleRegister : handleLogin} className="space-y-4">
            {isRegistering && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[#4A4A4A]">Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input-light"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#4A4A4A]">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#8A8A8A]" />
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-light pl-10"
                  data-testid="admin-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#4A4A4A]">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#8A8A8A]" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-light pl-10 pr-10"
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8A8A] hover:text-[#4A4A4A]"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {isRegistering && (
              <div className="space-y-2">
                <Label htmlFor="secret" className="text-[#4A4A4A]">Admin Secret</Label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#8A8A8A]" />
                  <Input
                    id="secret"
                    type="password"
                    placeholder="Enter admin secret"
                    value={adminSecret}
                    onChange={(e) => setAdminSecret(e.target.value)}
                    className="input-light pl-10"
                  />
                </div>
                <p className="text-xs text-[#8A8A8A]">Contact Deon for the admin secret</p>
              </div>
            )}

            <Button
              type="submit"
              className="w-full btn-gold py-5"
              disabled={loading}
              data-testid="admin-submit-btn"
            >
              {loading ? <span className="spinner" /> : (isRegistering ? 'Create Admin Account' : 'Sign In')}
            </Button>
          </form>

          <div className="text-center">
            <button
              onClick={() => setIsRegistering(!isRegistering)}
              className="text-[#D4AF37] hover:text-[#B4941F] text-sm font-medium"
            >
              {isRegistering ? 'Already have an admin account? Sign in' : 'Need to register as admin?'}
            </button>
          </div>

          <div className="pt-4 border-t border-[#E8E4DD]">
            <Link to="/" className="text-[#8A8A8A] hover:text-[#4A4A4A] text-sm flex items-center justify-center gap-1">
              ← Back to main site
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
    </>
  );
}
